import { z } from "zod";
import {
  optionalIsoDateString,
  optionalTrimmedString,
  optionalStringArray,
  trimmedString,
} from "./primitives";
import { assigneeTypeSchema } from "./assignments";

export const entityTypeSchema = z.enum(["ticket", "task", "incident", "request"]);
export const entityPrioritySchema = z.enum(["low", "medium", "high", "critical"]);
export const entityStatusSchema = z.enum([
  "new",
  "proposed",
  "approved",
  "in_progress",
  "done",
]);

export type EntityPriority = z.infer<typeof entityPrioritySchema>;

export const suggestedOwnerSchema = z.object({
  type: assigneeTypeSchema,
  id: trimmedString,
});

export const createEntitySchema = z.object({
  type: entityTypeSchema,
  title: trimmedString,
  description: trimmedString,
  priority: entityPrioritySchema,
  status: entityStatusSchema.optional(),
  threadId: optionalTrimmedString,
  systems: optionalStringArray,
  customerImpact: optionalTrimmedString,
  suggestedOwner: suggestedOwnerSchema.optional(),
  confidence: z
    .preprocess((value) => {
      if (value === null || value === undefined || value === "") {
        return undefined;
      }
      const parsed = typeof value === "number" ? value : Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }, z.number().min(0, "Confidence must be between 0 and 1.").max(1, "Confidence must be between 0 and 1."))
    .optional(),
});

export const updateEntitySchema = z.object({
  title: optionalTrimmedString,
  description: optionalTrimmedString,
  systems: optionalStringArray,
  customerImpact: optionalTrimmedString,
  suggestedOwner: suggestedOwnerSchema.optional(),
  confidence: z
    .preprocess((value) => {
      if (value === null || value === undefined || value === "") {
        return undefined;
      }
      const parsed = typeof value === "number" ? value : Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }, z.number().min(0, "Confidence must be between 0 and 1.").max(1, "Confidence must be between 0 and 1."))
    .optional(),
  priority: entityPrioritySchema.optional(),
  status: entityStatusSchema.optional(),
});

export const bulkReviewSchema = z.object({
  action: z.enum(["approve", "reject"]),
  ids: z.array(trimmedString).min(1, "At least one id is required."),
});

export const entityAuditExportQuerySchema = z.object({
  format: z.enum(["csv", "markdown"]).optional(),
  from: optionalIsoDateString,
  to: optionalIsoDateString,
});

export type CreateEntityPayload = z.infer<typeof createEntitySchema>;
export type UpdateEntityPayload = z.infer<typeof updateEntitySchema>;
export type BulkReviewPayload = z.infer<typeof bulkReviewSchema>;
export type EntityAuditExportQuery = z.infer<typeof entityAuditExportQuerySchema>;

export const routingRationaleSlaStateSchema = z.object({
  status: z.enum(["none", "on_track", "at_risk", "breached"]),
  targetAt: trimmedString.optional(),
  ruleName: trimmedString.optional(),
  minutesRemaining: z.number().int().nonnegative().optional(),
});

export const routingRationaleLoadSchema = z.object({
  teamOccupancy: z.number().int().nonnegative().optional(),
  agentOccupancy: z.number().int().nonnegative().optional(),
  fallbackQueueDepth: z.number().int().nonnegative().optional(),
});

export const routingRationaleSchema = z.object({
  decisionType: z.enum(["auto", "override", "manual"]),
  assigneeType: assigneeTypeSchema,
  assigneeId: trimmedString,
  intent: optionalTrimmedString,
  confidence: z.number().optional(),
  provider: optionalTrimmedString,
  modelVersion: optionalTrimmedString,
  promptVersion: optionalTrimmedString,
  requestId: optionalTrimmedString,
  fallbackUsed: z.boolean().optional(),
  cacheHit: z.boolean().optional(),
  guardrailFlags: z.array(trimmedString),
  slaState: routingRationaleSlaStateSchema,
  load: routingRationaleLoadSchema.optional(),
  overrideReason: optionalTrimmedString,
});

export const routingRationaleEntrySchema = z.object({
  eventId: trimmedString,
  createdAt: trimmedString,
  summary: trimmedString,
  actorType: trimmedString,
  actorId: trimmedString,
  routingRationale: routingRationaleSchema,
});

export const entityRoutingRationaleResponseSchema = z.object({
  entityId: trimmedString,
  rationales: z.array(routingRationaleEntrySchema),
});

export type RoutingRationale = z.infer<typeof routingRationaleSchema>;
export type RoutingRationaleEntry = z.infer<typeof routingRationaleEntrySchema>;
export type EntityRoutingRationaleResponse = z.infer<
  typeof entityRoutingRationaleResponseSchema
>;
